import { storableError } from '../../util/errors';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import {
  parseDateFromISO8601,
  getExclusiveEndDate,
  addTime,
  subtractTime,
  daysBetween,
  getStartOf,
} from '../../util/dates';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { LIBRARY_IMAGE_LISTING_FIELDS } from '../../util/listingLibraryImages';
import { isOriginInUse, isStockInUse } from '../../util/search';
import { parse } from '../../util/urlHelpers';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { denormalisedEntities } from '../../util/data';
import { fetchPrimaryEventsCatalog } from '../../util/primaryEventsQuery';
import {
  fetchDiscoverySearchResults,
  isBrowserRuntime,
} from '../../util/discoverySearchResults';
import { rehydrateDiscoveryResponse } from '../../util/discoverySdkRehydrate';
import { discoveryFilterFingerprint } from '../../util/discoveryFilterFingerprint';
import { pubEventDateUnixForSearch } from '../../util/eventDateRangeFilter';
import { mapSortParamForMarketplaceApi } from '../../util/searchSort';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 12 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 24;

/** Internal key on searchParams — not a URL filter; used for sort/page refresh detection. */
const DISCOVERY_LOCATION_SEARCH_KEY = '__discoveryLocationSearch';

const parseDiscoveryLocationSearch = locationSearch =>
  parse(locationSearch || '', {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

// ================ Action types ================ //

export const SEARCH_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/SearchPage/SEARCH_LISTINGS_ERROR';

export const SEARCH_MAP_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_MAP_LISTINGS_REQUEST';
export const SEARCH_MAP_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_MAP_LISTINGS_SUCCESS';
export const SEARCH_MAP_LISTINGS_ERROR = 'app/SearchPage/SEARCH_MAP_LISTINGS_ERROR';

export const SEARCH_MAP_SET_ACTIVE_LISTING = 'app/SearchPage/SEARCH_MAP_SET_ACTIVE_LISTING';

export const PRIMARY_EVENTS_SUCCESS = 'app/SearchPage/PRIMARY_EVENTS_SUCCESS';
export const PRIMARY_EVENTS_ERROR = 'app/SearchPage/PRIMARY_EVENTS_ERROR';

/** Accepts true / 1 / "true" / "TRUE" / trailing whitespace from .env / Docker env. */
const parseDiscoveryPrimaryEnv = v => {
  if (v === true || v === 1) {
    return true;
  }
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    return s === 'true' || s === '1' || s === 'yes';
  }
  return false;
};

export const isDiscoveryPrimaryEventsEnabled = parseDiscoveryPrimaryEnv(
  process.env.REACT_APP_PROMOTIX_DISCOVERY_PRIMARY_EVENTS
);

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
  primaryEvents: [],
  primaryEventsMeta: null,
  primaryEventsInProgress: false,
  primaryEventsError: null,
  mergedDiscoveryItems: null,
};

/**
 * Sort/page-only refresh: the visible result set's filters did not change, so we
 * keep the previously rendered cards in place while the server re-orders them.
 * Avoids a "Loading…" flash when changing sort or paging through results.
 */
const isSortOrPageRefresh = (state, locationSearch) => {
  const prev =
    state.searchParams?.[DISCOVERY_LOCATION_SEARCH_KEY] ||
    state.searchParams?.locationSearch;
  if (!prev || !state.mergedDiscoveryItems?.length) {
    return false;
  }
  return discoveryFilterFingerprint(prev) === discoveryFilterFingerprint(locationSearch);
};

const resultIds = data => data.data.map(l => l.id);

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_LISTINGS_REQUEST: {
      // Keep mergedDiscoveryItems on a sort/page-only refresh so the grid doesn't
      // blank to "Loading search results…" between requests. searchInProgress still
      // flips to true so the spinner appears briefly — keeping it false during a
      // pending request leaves stale state behind that other selectors rely on.
      const preserve = !!payload.preserveResults;
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        searchMapListingIds: [],
        searchListingsError: null,
        mergedDiscoveryItems: preserve ? state.mergedDiscoveryItems : null,
        ...(isDiscoveryPrimaryEventsEnabled
          ? {
              primaryEventsInProgress: preserve ? state.primaryEventsInProgress : true,
              primaryEventsError: null,
            }
          : {}),
      };
    }
    case SEARCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        mergedDiscoveryItems: payload.mergedDiscoveryItems ?? state.mergedDiscoveryItems,
        searchInProgress: false,
      };
    case SEARCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, searchInProgress: false, searchListingsError: payload };

    case SEARCH_MAP_SET_ACTIVE_LISTING:
      return {
        ...state,
        activeListingId: payload,
      };

    case PRIMARY_EVENTS_SUCCESS:
      return {
        ...state,
        primaryEventsInProgress: false,
        primaryEvents: payload.events || [],
        primaryEventsMeta: {
          totalPage: payload.total_page,
          totalRecord: payload.total_record,
          nextPage: payload.next_page,
        },
        primaryEventsError: null,
      };
    case PRIMARY_EVENTS_ERROR:
      // Keep last successful primary rows + meta so the UI count (and cards) do not
      // collapse after a transient refetch failure (common right after hydration).
      return {
        ...state,
        primaryEventsInProgress: false,
        primaryEventsError: payload,
      };
    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const searchListingsRequest = (searchParams, options = {}) => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams, preserveResults: !!options.preserveResults },
});

export const searchListingsSuccess = response => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: {
    data: response.data,
    mergedDiscoveryItems: response.mergedDiscoveryItems ?? null,
  },
});

export const searchListingsError = e => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

// Tracks the in-flight discovery search so a newer search (e.g. while the user
// drags the map and bounds change repeatedly) can abort the previous request
// instead of letting stale, superseded searches pile up on the single-threaded
// server. Module-scoped because only one discovery search is meaningful at a time.
let inflightDiscoveryController = null;

const isAbortError = e =>
  !!e && (e.name === 'AbortError' || e.code === 20 || e.code === 'ABORT_ERR');

/**
 * Merged PromoTix + TN search via GET /api/discovery/search-results.
 *
 * The server keeps both catalogs pre-sorted in RAM and caches filter sets, so the
 * request returns in tens of ms. We keep previous results visible during a sort/page
 * refresh so the UI never blanks to "Loading search results…".
 */
export const searchDiscoveryMerged = (locationSearch, config) => (dispatch, getState) => {
  const pageState = getState().SearchPage;
  const preserveResults = isSortOrPageRefresh(pageState, locationSearch);

  // Must match Flex search shape ({ sort, bounds, pub_* … }) so searchParamsPicker
  // can set searchParamsAreInSync — { locationSearch } alone left the UI stuck loading.
  const searchParams = {
    ...parseDiscoveryLocationSearch(locationSearch),
    [DISCOVERY_LOCATION_SEARCH_KEY]: locationSearch,
  };

  dispatch(searchListingsRequest(searchParams, { preserveResults }));

  const listingFields = config?.listing?.listingFields;
  const sanitizeConfig = { listingFields };

  // Cancel any previous in-flight discovery search before starting a new one.
  if (inflightDiscoveryController) {
    inflightDiscoveryController.abort();
  }
  const controller =
    typeof AbortController !== 'undefined' ? new AbortController() : null;
  inflightDiscoveryController = controller;

  return fetchDiscoverySearchResults(locationSearch, { signal: controller?.signal })
    .then(json => {
      const rawData = json && json.data;
      if (!rawData || !Array.isArray(rawData.items)) {
        throw new Error('Invalid discovery search response');
      }

      // Wire shape is plain JSON — `price` is { amount, currency }, ids are
      // { uuid }, geolocation is { lat, lng }. The SDK and downstream
      // components (formatMoney, ListingCard, SearchMap) require real SDK
      // type instances. Without this rehydration ListingCard throws
      // "Value must be a Money type" on every render of a TN listing.
      const d = rehydrateDiscoveryResponse(rawData);

      const listings = d.listings || [];
      const included = d.included || [];
      const meta = d.meta || {};
      const items = d.items || [];

      // Diagnostic: counts of what the server returned per page. Helps confirm
      // TN listings are actually being merged in vs. silently dropped.
      // eslint-disable-next-line no-console
      console.log('[discovery] response', {
        items: items.length,
        primaries: items.filter(i => i?.kind === 'primary').length,
        listings: items.filter(i => i?.kind === 'listing').length,
        wireListings: listings.length,
        included: included.length,
        meta,
      });

      dispatch(
        addMarketplaceEntities(
          { data: { data: listings, included, meta } },
          sanitizeConfig
        )
      );

      // Attach denormalised `images[]` for ListingCard, but keep the rehydrated
      // wire listing (SDK Money/UUID types). Replacing the whole listing with
      // Redux denorm breaks formatMoney ("Value must be a Money type").
      const entities = getState().marketplaceData.entities;
      const mergedDiscoveryItems = items.map(item => {
        if (item.kind !== 'listing' || !item.listing?.id) {
          return item;
        }
        const denorm = denormalisedEntities(
          entities,
          [{ id: item.listing.id, type: 'listing' }],
          false
        )[0];
        if (denorm?.images?.length) {
          return { kind: 'listing', listing: { ...item.listing, images: denorm.images } };
        }
        return item;
      });

      dispatch(
        searchListingsSuccess({
          data: { data: listings, included, meta },
          mergedDiscoveryItems,
        })
      );
      if (inflightDiscoveryController === controller) {
        inflightDiscoveryController = null;
      }
      return json;
    })
    .catch(e => {
      // A superseded search was aborted on purpose — the newer search now owns
      // the loading state, so don't surface it as a "Search failed" error.
      if (isAbortError(e)) {
        return null;
      }
      if (inflightDiscoveryController === controller) {
        inflightDiscoveryController = null;
      }
      dispatch(searchListingsError(storableError(e)));
      throw e;
    });
};

export const searchListings = (searchParams, config) => (dispatch, getState, sdk) => {
  dispatch(searchListingsRequest(searchParams));

  // SearchPage can enforce listing query to only those listings with valid listingType
  // NOTE: this only works if you have set 'enum' type search schema to listing's public data fields
  //       - listingType
  //       Same setup could be expanded to 2 other extended data fields:
  //       - transactionProcessAlias
  //       - unitType
  //       ...and then turned enforceValidListingType config to true in configListing.js
  // Read More:
  // https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
  const searchValidListingTypes = listingTypes => {
    return config.listing.enforceValidListingType
      ? {
          pub_listingType: listingTypes.map(l => l.listingType),
          // pub_transactionProcessAlias: listingTypes.map(l => l.transactionType.alias),
          // pub_unitType: listingTypes.map(l => l.transactionType.unitType),
        }
      : {};
  };

  const priceSearchParams = priceParam => {
    const inSubunits = value => convertUnitToSubUnit(value, unitDivisor(config.currency));
    const values = priceParam ? priceParam.split(',') : [];
    return priceParam && values.length === 2
      ? {
          price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
        }
      : {};
  };

  const datesSearchParams = datesParam => {
    const searchTZ = 'Etc/UTC';
    const datesFilter = config.search.defaultFilters.find(f => f.key === 'dates');
    const values = datesParam ? datesParam.split(',') : [];
    const hasValues = datesFilter && datesParam && values.length === 2;
    const { dateRangeMode, availability } = datesFilter || {};
    const isNightlyMode = dateRangeMode === 'night';
    const isEntireRangeAvailable = availability === 'time-full';

    // SearchPage need to use a single time zone but listings can have different time zones
    // We need to expand/prolong the time window (start & end) to cover other time zones too.
    //
    // NOTE: you might want to consider changing UI so that
    //   1) location is always asked first before date range
    //   2) use some 3rd party service to convert location to time zone (IANA tz name)
    //   3) Make exact dates filtering against that specific time zone
    //   This setup would be better for dates filter,
    //   but it enforces a UX where location is always asked first and therefore configurability
    const getProlongedStart = date => subtractTime(date, 14, 'hours', searchTZ);
    const getProlongedEnd = date => addTime(date, 12, 'hours', searchTZ);

    const startDate = hasValues ? parseDateFromISO8601(values[0], searchTZ) : null;
    const endRaw = hasValues ? parseDateFromISO8601(values[1], searchTZ) : null;
    const endDate =
      hasValues && isNightlyMode
        ? endRaw
        : hasValues
        ? getExclusiveEndDate(endRaw, searchTZ)
        : null;

    const today = getStartOf(new Date(), 'day', searchTZ);
    const possibleStartDate = subtractTime(today, 14, 'hours', searchTZ);
    const hasValidDates =
      hasValues &&
      startDate.getTime() >= possibleStartDate.getTime() &&
      startDate.getTime() <= endDate.getTime();

    const dayCount = isEntireRangeAvailable ? daysBetween(startDate, endDate) : 1;
    const day = 1440;
    const hour = 60;
    // When entire range is required to be available, we count minutes of included date range,
    // but there's a need to subtract one hour due to possibility of daylight saving time.
    // If partial range is needed, then we just make sure that the shortest time unit supported
    // is available within the range.
    // You might want to customize this to match with your time units (e.g. day: 1440 - 60)
    const minDuration = isEntireRangeAvailable ? dayCount * day - hour : hour;
    return hasValidDates
      ? {
          start: getProlongedStart(startDate),
          end: getProlongedEnd(endDate),
          // Availability can be time-full or time-partial.
          // However, due to prolonged time window, we need to use time-partial.
          availability: 'time-partial',
          // minDuration uses minutes
          minDuration,
        }
      : {};
  };

  const { perPage, price, dates, eventDates, sort, address, ...rest } = searchParams;
  const priceMaybe = priceSearchParams(price);
  const datesMaybe = datesSearchParams(dates);
  const eventDatesMaybe = pubEventDateUnixForSearch(eventDates);
  const sortConfig = config.search.sortConfig;
  const sortContext = { ...rest, address };
  const apiSort = mapSortParamForMarketplaceApi(sort, sortConfig, sortContext);
  const sortMaybe = apiSort ? { sort: apiSort } : {};

  const params = {
    ...rest,
    ...priceMaybe,
    ...datesMaybe,
    ...eventDatesMaybe,
    ...sortMaybe,
    ...searchValidListingTypes(config.listing.listingTypes),
    perPage,
  };

  return sdk.listings
    .query(params)
    .then(response => {
      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(searchListingsSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(searchListingsError(storableError(e)));
      throw e;
    });
};

export const setActiveListing = listingId => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});

/**
 * Fetch the full primary events catalog in a single request.
 *
 * The server keeps a pre-warmed in-memory catalog (refreshed hourly), so this
 * call returns instantly from RAM — no upstream API calls, no progressive
 * loading, no background setTimeout chains.
 *
 * If the catalog is already in Redux (e.g. from SSR or a previous load),
 * re-dispatch it immediately to clear the primaryEventsInProgress flag
 * without any network call.
 */
export const fetchPrimaryEvents = () => (dispatch, getState) => {
  if (!isDiscoveryPrimaryEventsEnabled) {
    return Promise.resolve();
  }

  const { primaryEvents: existing, primaryEventsMeta: meta } = getState().SearchPage;
  if (existing && existing.length > 0 && meta) {
    dispatch({
      type: PRIMARY_EVENTS_SUCCESS,
      payload: {
        events: existing,
        total_page: meta.totalPage,
        total_record: meta.totalRecord,
        next_page: meta.nextPage,
      },
    });
    return Promise.resolve();
  }

  return fetchPrimaryEventsCatalog()
    .then(json => {
      const d = json && json.data;
      if (!d || !Array.isArray(d.events)) {
        throw new Error('Invalid catalog response');
      }
      dispatch({ type: PRIMARY_EVENTS_SUCCESS, payload: d });
      // eslint-disable-next-line no-console
      console.info('[Discovery] catalog loaded:', d.events.length, 'events');
    })
    .catch(e => {
      // eslint-disable-next-line no-console
      console.error('[Discovery] catalog fetch failed:', e && e.message ? e.message : e);
      dispatch({
        type: PRIMARY_EVENTS_ERROR,
        payload: storableError(e),
      });
    });
};

export const loadData = (params, search, config) => {
  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });


  

  // Add minStock filter with default value (1), if stock management is in use.
  // This can be overwriten with passed-in query parameters.
  const minStockMaybe = isStockInUse(config) ? { minStock: 1 } : {};
  const { page = 1, address, origin, ...rest } = queryParams;
  const originMaybe = isOriginInUse(config) && origin ? { origin } : {};

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  const flexSearch = searchListings(
    {
      ...minStockMaybe,
      ...rest,
      ...originMaybe,
      // Used only to pick the implicit search sort (soonest event). Stripped before the SDK call.
      address,
      page,
      perPage: RESULT_PAGE_SIZE,
      pub_closeListingExpOffer:false,
      include: ['author', 'images'],
      'fields.listing': [
        'title',
        'geolocation',
        'price',
        'createdAt',
        'publicData.listingType',
        'publicData.transactionProcessAlias',
        'publicData.unitType',
        // These help rendering of 'purchase' listings,
        // when transitioning from search page to listing page
        'publicData.pickupEnabled',
        'publicData.shippingEnabled',
        // Search card date line (TEAK listing wizard / EditListingWizard)
        'publicData.eventDate',
        'publicData.eventDateUnix',
        'publicData.eventStartDate',
        ...LIBRARY_IMAGE_LISTING_FIELDS,
      ],
      'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
      'fields.image': [
        'variants.scaled-small',
        'variants.scaled-medium',
        `variants.${variantPrefix}`,
        `variants.${variantPrefix}-2x`,
      ],
      ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
      ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
      'limit.images': 1,
    },
    config
  );

  return (dispatch, getState, sdk) => {
    if (!isDiscoveryPrimaryEventsEnabled) {
      return dispatch(flexSearch);
    }
    // During SSR we cannot call our own `/api/discovery/search-results` endpoint
    // (no browser `fetch` origin, no auth cookies) — `RouteComponentRenderer`
    // re-runs `loadData` on the client after hydration via `componentDidMount`,
    // and that is where the merged search actually fires.
    if (!isBrowserRuntime()) {
      return dispatch(fetchPrimaryEvents());
    }
    const mergedPromise = dispatch(searchDiscoveryMerged(search, config));
    const primaryPromise = dispatch(fetchPrimaryEvents());
    return Promise.all([mergedPromise, primaryPromise]);
  };
};
